/**
 * @fileoverview ListTypeSelector Component
 * Allows users to change the type of a list via an inline dropdown
 */

// External imports
import React from 'react';

// UI Components
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Internal imports
import { useUpdateList } from '../hooks/use-lists';
import { LIST_TYPE_OPTIONS, getListTypeInfo } from '../constants/list-types';

interface ListTypeSelectorProps {
  listId: string;
  currentType: string;
  variant?: 'badge' | 'inline';
  onUpdateStart?: () => void;
  onUpdateComplete?: () => void;
}

export const ListTypeSelector: React.FC<ListTypeSelectorProps> = ({
  listId,
  currentType,
  variant = 'inline',
  onUpdateStart,
  onUpdateComplete,
}) => {
  const updateList = useUpdateList();
  const currentTypeInfo = getListTypeInfo(currentType);

  const handleTypeChange = async (newType: string) => {
    if (newType === currentType) return;

    try {
      onUpdateStart?.();
      await updateList.mutateAsync({
        listId,
        data: { listType: newType },
      });
      onUpdateComplete?.();
    } catch (error) {
      console.error('Failed to update list type:', error);
    }
  };

  if (variant === 'badge') {
    return (
      <Select
        value={currentType}
        onValueChange={handleTypeChange}
        disabled={updateList.isPending}
      >
        <SelectTrigger
          className={`h-auto w-auto border-0 focus:ring-0 focus:ring-offset-0 ${currentTypeInfo.color} px-2 py-1 text-xs font-medium rounded-md`}
          onClick={(e) => e.stopPropagation()}
        >
          <SelectValue>
            {updateList.isPending ? 'Updating...' : currentTypeInfo.label}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {LIST_TYPE_OPTIONS.map((type) => (
            <SelectItem key={type.value} value={type.value}>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${type.color}`}></div>
                <span>{type.label}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Select
      value={currentType}
      onValueChange={handleTypeChange}
      disabled={updateList.isPending}
    >
      <SelectTrigger
        className="w-[180px]"
        onClick={(e) => e.stopPropagation()}
      >
        <SelectValue>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${currentTypeInfo.color}`}></div>
            <span>{updateList.isPending ? 'Updating...' : currentTypeInfo.label}</span>
          </div>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {LIST_TYPE_OPTIONS.map((type) => (
          <SelectItem key={type.value} value={type.value}>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${type.color}`}></div>
              <span>{type.label}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
